/** Which figure filled a job's missing revenue (zeecore revenueFillFor, plan step 3). */
export type RevenueFillBasis =
  | 'avg_per_job'
  | 'inbound_allowance'
  | 'xero_monthly_final'
  | 'xero_monthly_provisional'
  | 'xero_carried_forward';

export interface TruthMergedCostRow {
  date: string;
  vehicle: string;
  daily_miles: number;
  driving_hours: number;
  waiting_hours: number;
  driver_cost: number;
  jigsaw_fuel_cost: number;
  jigsaw_fuel_quantity: number;
  jigsaw_fuel_unitPrice: number;
  revenue: number;
  order_count?: number;
  pickup_count?: number;
  delivery_count?: number;
  revenue_breakdown?: Array<{
    order_entity_id: string;
    order_name?: string;
    revenue: number;
    imputed?: boolean;
    attribution_tier?:
      | 'declared_same_day'
      | 'note_and_gps'
      | 'note_only'
      | 'planned'
      | 'unknown';
    revenue_source?: 'OrderObserved' | 'OrderDeclared' | 'none';
    leg_role?: 'collection' | 'trunk' | 'delivery' | 'unknown';
    /** Hub depot number from getnotes on this vehicle's ORDER_ON_VEHICLE edge. */
    handoff_depot?: string;
    /** Network cost charged on this leg (outbound, non-trunk) when deducted. */
    network_cost_gbp?: number;
    /** Which network figure the charge used, and its month (YYYY-MM). */
    network_cost_basis?: string;
    network_cost_period?: string;
    /** Read-time fill: revenue was missing and the Configuration figure was used. */
    assumed_avg_per_job?: boolean;
    revenue_fill_basis?: RevenueFillBasis;
    /** The month whose Xero figure filled the revenue (YYYY-MM), for the monthly bases. */
    revenue_fill_period?: string;
  }>;
  vehicle_type?: string;
  depreciation?: number;
  total_cost: number;
  profit: number;
  profit_margin: number;
  /**
   * No telematics that day: miles and mileage-based costs are unknown (not
   * zero), so profit and margin are not meaningful. Absent on older responses.
   */
  telematics_missing?: boolean;
  /**
   * Pallet-network cost of this day's outbound legs, already inside
   * total_cost and profit (zeecore deducts it at read time). Absent when
   * network cost is not deducted.
   */
  network_cost_gbp?: number;
  /** Legs whose network cost is unknown (outbound with no rate, or no depots), not £0. */
  network_cost_unknown_legs?: number;
}

/** Per-order row from zeecore /profitability/v2/merged-orders (merged_orders.csv shape). */
export interface TruthMergedOrderRow {
  order_id: string;
  status: string;
  order_import_integration_type: string;
  customer: string;
  goods_weight: string;
  goods_quantity: string;
  origin_end_timestamp_local: string;
  destination_end_timestamp_local: string;
  vehicle_assigned: string;
  /** Pick-up / trunk vehicle when multi-leg; empty when sole delivery carrier. */
  vehicle_pickup: string;
  /**
   * Hub depot number from getnotes on the pick-up/trunk ORDER_ON_VEHICLE edge
   * (e.g. `"102"`). Used for map Handoff pin via org depot lookup.
   */
  handoff_depot?: string;
  /** Delivery-leg carrying vehicle (final ORDER_ON_VEHICLE window). */
  vehicle_observed: string;
  origin_postal_code: string;
  origin_city: string;
  destination_postal_code: string;
  destination_city: string;
  service_level_name: string;
  total_revenue_tenant_currency: string;
  tenant_currency: string;
  revenue_imputed: number;
  /** How filled revenue was set when revenue_imputed is 1: the average £/job or the inbound delivery allowance. */
  revenue_fill_basis?: RevenueFillBasis;
    /** The month whose Xero figure filled the revenue (YYYY-MM), for the monthly bases. */
    revenue_fill_period?: string;
  origin_latitude: number | null;
  origin_longitude: number | null;
  destination_latitude: number | null;
  destination_longitude: number | null;
  /** 'postcode_centroid' = approximate position filled from the postcode. */
  origin_coords_source?: 'postcode_centroid';
  destination_coords_source?: 'postcode_centroid';
  data_origin?: {
    origin: 'computed_from_events' | 'imported_from_legacy' | 'reconstructed_from_truth';
    materialised_at: string;
  };
  allocated_cost_gbp?: number;
  allocated_miles?: number;
  profit_gbp?: number;
  allocation_method?: 'marginal' | 'fallback_equal_split';
  marginal_cost_share?: number;
  allocation_vehicle_day_date?: string;
  /** Costed on a vehicle-day with no telematics: cost understated, profit unknown. */
  telematics_missing?: boolean;
  /** Read-time pallet-network cost (zeecore networkCost.ts). Outbound only. */
  network_direction?: 'outbound' | 'inbound' | 'local' | 'unknown';
  /**
   * Who collected / delivered the job (zeecore orderLegVehicles.ts, by
   * direction): our vehicle, or another depot's number. '' = not ours /
   * not yet / unknown. Absent on older API responses.
   */
  collected_by_vehicle?: string;
  collected_by_depot?: string;
  delivered_by_vehicle?: string;
  delivered_by_depot?: string;
  /** Our vehicle that carried it between hub and depot (neither collected nor delivered). */
  trunk_vehicle?: string;
  /** zeecore's classification of `status` (palletwaysJobStatus.ts); null = unclassified. */
  status_kind?: 'progress' | 'done' | null;
  /** Absent = unknown (outbound with no rate, or no depots), not £0. */
  network_cost_gbp?: number;
  network_cost_low_gbp?: number;
  network_cost_high_gbp?: number;
  network_cost_basis?: string;
  network_cost_period?: string;
  /** profit_gbp − network cost; absent when either is unknown. */
  profit_after_network_gbp?: number;
  profit_after_network_low_gbp?: number;
  profit_after_network_high_gbp?: number;
}

/** Customer monthly rollup from zeecore /profitability/v2/customer-profitability-monthly. */
export interface TruthCustomerMonthlyRow {
  period: string;
  customer_name: string;
  order_count: number;
  /** Sum of lifts/pallets for distinct orders (Phase 1B+). */
  total_lifts?: number;
  total_revenue_gbp: number;
  /** Read-time: revenue was missing and filled at the single average (customer-day rows have no direction). */
  revenue_assumed_avg_per_job?: boolean;
  total_allocated_cost_gbp: number;
  total_allocated_miles: number;
  profit_gbp: number;
  profit_margin_pct: number;
  avg_revenue_per_order_gbp: number;
  avg_cost_per_order_gbp: number;
  avg_profit_per_order_gbp: number;
  cost_per_mile_gbp: number;
  /** Orders costed on a no-GPS vehicle-day (cost understated). Absent on older rows. */
  orders_without_telematics?: number;
}

/** Coverage of read-time network cost on /merged-orders (null when not configured). */
export interface TruthNetworkCostSummary {
  orders: number;
  by_direction: Record<'outbound' | 'inbound' | 'local' | 'unknown', number>;
  /** Of by_direction.unknown: no depots / depots but neither is ours. */
  unknown_no_depots?: number;
  unknown_not_home_depot?: number;
  by_basis: Record<string, number>;
  outbound_unknown_cost: number;
  total_network_cost_gbp: number;
  total_network_cost_low_gbp: number;
  total_network_cost_high_gbp: number;
  periods_used: Record<string, string>;
  /** Inbound allowance configured: profit after network cost is the headline. */
  headline_ready?: boolean;
  /** Complete months in the window: what is in view vs the month's declared Xero inputs. */
  xero_reconciliation?: Array<{
    month: string;
    outbound_orders_in_view: number;
    outbound_consignments_declared: number;
    network_cost_in_view_gbp: number;
    palletways_charges_gbp: number;
    coverage_pct: number;
  }>;
}

/** Customer daily rollup from zeecore /profitability/v2/customer-profitability-daily (`period` = YYYY-MM-DD). */
export type TruthCustomerDailyRow = TruthCustomerMonthlyRow;

/**
 * Jobs carried in the window that no customer-daily row includes yet — their
 * delivery day is not computed, or the order id never joins merged-orders.
 * Computed by zeecore at read time (customer-profitability-daily).
 */
export interface TruthUnplacedAllocations {
  jobs: number;
  allocation_rows: number;
  allocated_cost_gbp: number;
  revenue_gbp: number;
}

/**
 * Allocations carried in a window that no customer row includes: jobs with no
 * delivery recorded yet (top level), and legs carried further from the
 * recorded delivery date than the roll-up counts.
 */
export interface TruthAwaitingMergedOrderSummary extends TruthUnplacedAllocations {
  /** Absent from responses before 2026-10-18. */
  outside_delivery_range?: TruthUnplacedAllocations & { days_before: number; days_after: number };
}

export interface TruthDataSourceInfo {
  source: string;
  event_type: string;
  entity_count: number | null;
  event_count: number | null;
  status: 'available' | 'unavailable' | 'unknown';
}

export interface TruthLastComputedInfo {
  computed_at?: string;
  computedAt?: string;
  record_count?: number;
  recordCount?: number;
  start_date?: string;
  startDate?: string;
  end_date?: string;
  endDate?: string;
  /** Assumptions fingerprint stamped at compute time */
  assumptions_content_hash?: string;
  config_revision?: number;
}

export interface TruthCoverageSummary {
  total_vehicle_days: number;
  profitability_ready_vehicle_days: number;
  vehicles_with_any_gap: number;
  gap_counts: Record<string, number>;
  dimension_status_counts?: {
    mileage: Record<string, number>;
    revenue: Record<string, number>;
    fuel: Record<string, number>;
    driver: Record<string, number>;
  };
}

export interface TruthStatusResponse {
  organisation_id: string;
  date_range: { start: string; end: string };
  sources: TruthDataSourceInfo[];
  warnings: string[];
  last_computed: TruthLastComputedInfo | null;
  coverage_summary?: TruthCoverageSummary | null;
  active_job?: TruthComputeJobRecord | null;
  /** Stops/map overlay availability from telematics-stops manifest (last compute). */
  telematics_stops?: {
    record_count: number | null;
    vehicle_days_with_stops: number | null;
    computed_at: string | null;
    start_date: string | null;
    end_date: string | null;
    overlaps_window?: boolean;
  } | null;
}

/** Delivery stop row for truth Vehicle Map / MapPin (zeecore telematics-stops). */
export interface TruthTelematicsDeliveryStop {
  AssetName: string;
  day_date: string;
  stop_number: number;
  arrival_time: string;
  departure_time: string;
  duration_minutes: number;
  postcode: string;
  latitude: number;
  longitude: number;
  stop_type: string;
  miles_to_next_stop: number;
}

/** Route segment polyline for truth Vehicle Map (zeecore telematics-segments). */
export interface TruthTelematicsRouteSegment {
  vehicle: string;
  date: string;
  from_stop_number: number;
  to_stop_number: number;
  segment_start?: string;
  segment_end?: string;
  from_stop?: { lat: number; lon: number; postcode?: string };
  to_stop?: { lat: number; lon: number; postcode?: string };
  n_points_raw?: number;
  n_points_kept?: number;
  points: [number, number][];
}

/** Which profitability projection phases a compute job runs. */
export type TruthComputeScope = 'all' | 'vehicle-day' | 'order' | 'customer';

export interface TruthComputeJobRecord {
  jobId: string;
  startDate: string;
  endDate: string;
  /** Defaults to 'all' for jobs created before scope was introduced. */
  scope?: TruthComputeScope;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  recordCount?: number;
  error?: string;
  progress?: {
    phase: string;
    percentComplete: number;
    vehicleIndex?: number;
    totalVehicles?: number;
    processedVehicleDays?: number;
    /** Multi-day vehicle-day phase: day being computed (1-based of totalDays). */
    currentDay?: string;
    dayIndex?: number;
    totalDays?: number;
    totalVehicleDays?: number;
    currentVehicle?: string;
    lastProgressAt: string;
  };
}

export interface TruthCoverageReport {
  organisation_id: string;
  date_range: { start: string; end: string };
  computed_at: string;
  summary: TruthCoverageSummary;
}

/** Vehicle types used in zeecore profitability assumptions (body style). */
export type ProfitabilityVehicleType =
  | 'Lorry'
  | 'Tractor Unit'
  | 'Mini Truck'
  | 'Service Van'
  | 'Rigid Truck'
  | 'Default';

export const PROFITABILITY_VEHICLE_TYPES: ProfitabilityVehicleType[] = [
  'Lorry',
  'Tractor Unit',
  'Mini Truck',
  'Service Van',
  'Rigid Truck',
  'Default',
];

export type ProfitabilityTonnageClass = '3.5T' | '7.5T' | '18T' | '26T' | 'Artic' | 'Default';

export const PROFITABILITY_TONNAGE_CLASSES: ProfitabilityTonnageClass[] = [
  '3.5T',
  '7.5T',
  '18T',
  '26T',
  'Artic',
  'Default',
];

export type ProfitabilityCostRateBasis = 'body_style' | 'tonnage';

export type ProfitabilityFuelSource = 'mileage_rates' | 'jigsaw';

export type ProfitabilityStandingCostPolicy = 'calendar_day' | 'active_day';

export interface ProfitabilityDriverCostRate {
  mileage_rate: number;
  driving_hourly_rate: number;
  waiting_hourly_rate: number;
}

/** Canonical assumptions persisted at zeecore /profitability/v2/config. */
export interface ProfitabilityAssumptionsConfig {
  version: string;
  updated_at: string;
  updated_by: string;
  /** Monotonic revision — increments when assumption payload changes */
  revision?: number;
  /** Short SHA-256 of assumption payload (excludes meta fields) */
  content_hash?: string;
  fuel_source: ProfitabilityFuelSource;
  standing_cost_policy: ProfitabilityStandingCostPolicy;
  /** Apply fuel / standing / driver rates by body style or tonnage class. */
  cost_rate_basis?: ProfitabilityCostRateBasis;
  fuel_cost: Record<ProfitabilityVehicleType, number>;
  depreciation: Record<ProfitabilityVehicleType, number>;
  driver_cost: Record<ProfitabilityVehicleType, ProfitabilityDriverCostRate>;
  fuel_cost_tonnage?: Record<ProfitabilityTonnageClass, number>;
  /**
   * Optional UK imperial MPG by tonnage class. Editing MPG in Config derives
   * `fuel_cost_tonnage` from the latest uploaded Keyfuels PPL. Compute still
   * reads £/mi only.
   */
  fuel_mpg_tonnage?: Partial<Record<ProfitabilityTonnageClass, number>>;
  depreciation_tonnage?: Record<ProfitabilityTonnageClass, number>;
  driver_cost_tonnage?: Record<ProfitabilityTonnageClass, ProfitabilityDriverCostRate>;
  /** Tyres £/mi by body style */
  tyre_cost?: Record<ProfitabilityVehicleType, number>;
  /** Variable repairs £/mi by body style */
  variable_repair_cost?: Record<ProfitabilityVehicleType, number>;
  /** Tyres £/mi by tonnage class */
  tyre_cost_tonnage?: Record<ProfitabilityTonnageClass, number>;
  /** Variable repairs £/mi by tonnage class */
  variable_repair_cost_tonnage?: Record<ProfitabilityTonnageClass, number>;
  /**
   * Confirmed running-rate stamps (key → value). Same semantics as fleet-cost
   * rate_confirmations — Confirm rates on the Running tab writes here.
   */
  rate_confirmations?: Record<string, number>;
}
